import React, { FunctionComponent, useEffect, useState } from 'react';
import {
  buildResultList,
  buildPager,
  Pager as HeadlessPager,
  ResultList,
  Result,
} from '@coveo/headless';
import { useCoveoEngine } from 'lib/utils/coveo/SearchContext/CoveoEngineContext';
import { SvgIcon } from 'src/helpers/SvgIcon';

interface PaginationProps {
  onPageChange?: (newResults: Result[]) => void;
  className?: string;
  fieldsToInclude?: string[];
}

export const Pagination: FunctionComponent<PaginationProps> = ({
  onPageChange,
  className = '',
  fieldsToInclude = [
    'cwfirstname',
    'cwlastname',
    'cwdegreetitles',
    'cwsearchspecialties',
    'cwacceptingnewpatients',
    'cwphoto',
    'cwaddress1',
  ],
}) => {
  const engine = useCoveoEngine();

  const [pager, setPager] = useState<HeadlessPager | null>(null);
  const [resultList, setResultList] = useState<ResultList | null>(null);

  const scrollToTop = () => {
    const target = document.querySelector('.coveo-search-list');
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    if (!engine) return;
    const newPager = buildPager(engine, { options: { numberOfPages: 5 } });
    const newResultList = buildResultList(engine, { options: { fieldsToInclude } });
    setPager(newPager);
    setResultList(newResultList);
  }, [engine]);

  const [state, setState] = useState(pager?.state ?? null);

  useEffect(() => {
    if (!pager || !resultList) return;

    const unsubscribePager = pager.subscribe(() => {
      setState(pager.state);
      // logic to persist page number on page load - start
      const currentPage = pager.state.currentPages.find((p) => pager.isCurrentPage(p));

      if (currentPage) {
        sessionStorage.setItem('currentCoveoPage', currentPage.toString());
      }
      // logic to persist page number on page load - end
    });
    const unsubscribeResults = resultList.subscribe(() => {
      onPageChange?.(resultList.state.results);
    });

    return () => {
      unsubscribePager();
      unsubscribeResults();
    };
  }, [pager, resultList, onPageChange]);

  if (!pager || !state || state.currentPages.length <= 1) {
    return null;
  }

  return (
    <nav className={`flex items-center justify-center gap-2 ${className}`}>
      <button
        disabled={!state.hasPreviousPage}
        onClick={() => {
          pager.previousPage();
          setTimeout(() => scrollToTop(), 0);
        }}
        className={`group w-24 h-24 flex items-center text-blue-base justify-center rounded-sm hover:bg-blue-100 transition ${
          !state.hasPreviousPage ? 'opacity-0 cursor-not-allowed' : ''
        }`}
        title="Previous"
        aria-label="Previous Page"
      >
        <SvgIcon icon="chevron-left" size="md" />
      </button>

      {state.currentPages.map((page) => (
        <button
          key={page}
          disabled={pager.isCurrentPage(page)}
          onClick={() => {
            pager.selectPage(page);
            setTimeout(() => scrollToTop(), 0);
          }}
          className={`group w-24 h-24 font-bold flex items-center text-blue-base justify-center rounded-sm hover:bg-blue-100 transition ${
            pager.isCurrentPage(page)
              ? 'bg-blue-600 text-white border-blue-600'
              : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'
          }`}
          title={`Page ${page}`}
          aria-label={pager.isCurrentPage(page) ? `page ${page}` : undefined}
        >
          {page}
        </button>
      ))}

      <button
        disabled={!state.hasNextPage}
        onClick={() => {
          pager.nextPage();
          setTimeout(() => scrollToTop(), 0);
        }}
        className={`group w-24 h-24 font-bold flex items-center text-blue-base justify-center rounded-sm hover:bg-blue-100 transition ${
          !state.hasNextPage ? 'opacity-0 cursor-not-allowed' : ''
        }`}
        title="Next"
        aria-label="Next Page"
      >
        <SvgIcon icon="chevron-right" size="md" />
      </button>
    </nav>
  );
};

export default Pagination;
